import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Input,
  Modal,
  Form,
  Select,
  InputNumber,
  Switch,
  Tag,
  Space,
  Tooltip,
  Popconfirm,
  Typography,
  message,
  Row,
  Col,
  Card,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  CopyOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  LineChartOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { productosService, categoriasService, proveedoresService } from '../services/api';
import { useAuth } from '../context/AuthContext';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const UNIDADES = ['PZA', 'KG', 'LT', 'MT', 'CM', 'M2', 'M3', 'CJ', 'PAQ', 'BTE', 'PAR', 'SET', 'ROLLO', 'OTRO'];

const formatCurrency = (amount) => {
  if (amount == null || amount === '') return '—';
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2
  }).format(amount);
};

function getStockColor(stockActual, stockMinimo, stockMaximo) {
  const actual = parseFloat(stockActual) || 0;
  const minimo = parseFloat(stockMinimo) || 0;
  const maximo = parseFloat(stockMaximo) || 0;
  if (actual <= minimo) return { color: '#ff4d4f', class: 'stock-critico' };
  if (maximo > 0 && actual >= maximo) return { color: '#722ed1', class: 'stock-sobre' };
  if (actual <= minimo * 1.5) return { color: '#faad14', class: 'stock-bajo' };
  return { color: '#52c41a', class: 'stock-ok' };
}

function Productos() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = user?.rol === 'root' || user?.rol === 'admin';
  const canDelete = user?.rol === 'root';

  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [proveedorFilter, setProveedorFilter] = useState(null);
  const [form] = Form.useForm();

  const fetchProductos = useCallback(async (overrides = {}) => {
    setLoading(true);
    try {
      const s = 'search' in overrides ? overrides.search : search;
      const prov = 'proveedor_id' in overrides ? overrides.proveedor_id : proveedorFilter;

      const params = {};
      if (s) params.search = s;
      if (prov) params.proveedor_id = prov;

      const res = await productosService.getAll(params);
      setProductos(res.data.data || []);
    } catch (error) {
      message.error(error.displayMessage || 'Error al cargar productos');
    } finally {
      setLoading(false);
    }
  }, [search, proveedorFilter]);

  const fetchCategorias = useCallback(async () => {
    try {
      const res = await categoriasService.getAll();
      setCategorias(res.data.data || []);
    } catch {
      // silent
    }
  }, []);

  const fetchProveedores = useCallback(async () => {
    try {
      const res = await proveedoresService.getAll();
      setProveedores(res.data.data || []);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchProductos();
    fetchCategorias();
    fetchProveedores();
  }, [fetchProductos, fetchCategorias, fetchProveedores]);

  const handleSearch = (value) => {
    setSearch(value);
    fetchProductos({ search: value });
  };

  const handleProveedorFilter = (value) => {
    setProveedorFilter(value || null);
    fetchProductos({ proveedor_id: value || null });
  };

  const openCreateModal = () => {
    setEditingProduct(null);
    form.resetFields();
    form.setFieldsValue({ activo: true, unidad_medida: 'PZA' });
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingProduct(record);
    form.setFieldsValue({
      clave: record.clave,
      nombre: record.nombre,
      descripcion: record.descripcion,
      categoria_id: record.categoria_id,
      proveedor_id: record.proveedor_id,
      unidad_medida: record.unidad_medida,
      unidad_tendencia: record.unidad_tendencia || undefined,
      factor_unidad_tendencia: record.factor_unidad_tendencia != null ? parseFloat(record.factor_unidad_tendencia) : undefined,
      stock_minimo: parseFloat(record.stock_minimo),
      stock_maximo: parseFloat(record.stock_maximo),
      precio_compra: parseFloat(record.precio_compra),
      precio_venta: parseFloat(record.precio_venta),
      activo: Boolean(record.activo)
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      // Ensure clave is uppercase
      if (values.clave) values.clave = values.clave.toUpperCase();

      // Unidad de tendencia y factor van juntos: si falta uno, se limpian ambos.
      if (!values.unidad_tendencia || !values.factor_unidad_tendencia) {
        values.unidad_tendencia = null;
        values.factor_unidad_tendencia = null;
      }

      setSaving(true);
      if (editingProduct) {
        await productosService.update(editingProduct.id, values);
        message.success('Producto actualizado exitosamente');
      } else {
        await productosService.create(values);
        message.success('Producto creado exitosamente');
      }
      setModalOpen(false);
      fetchProductos();
    } catch (error) {
      if (error.errorFields) return; // validation error
      message.error(error.displayMessage || 'Error al guardar producto');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      await productosService.delete(record.id);
      message.success(`Producto "${record.nombre}" desactivado`);
      fetchProductos();
    } catch (error) {
      message.error(error.displayMessage || 'Error al eliminar producto');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(`Clave "${text}" copiada al portapapeles`);
    });
  };

  const columns = [
    {
      title: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 130,
      sorter: (a, b) => a.clave.localeCompare(b.clave),
      render: (clave) => (
        <Space size={4}>
          <Text code style={{ fontSize: 12 }}>{clave}</Text>
          <Tooltip title="Copiar clave">
            <CopyOutlined
              style={{ cursor: 'pointer', color: '#1677ff', fontSize: 12 }}
              onClick={() => copyToClipboard(clave)}
            />
          </Tooltip>
        </Space>
      )
    },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      sorter: (a, b) => a.nombre.localeCompare(b.nombre),
      ellipsis: true,
      render: (nombre, record) => (
        <Tooltip title={record.descripcion || nombre}>
          <span>{nombre}</span>
        </Tooltip>
      )
    },
    {
      title: 'Categoría',
      key: 'categoria_nombre',
      width: 130,
      render: (_, record) => record.categoria?.nombre
        ? <Tag>{record.categoria.nombre}</Tag>
        : <Text type="secondary">—</Text>
    },
    {
      title: 'Proveedor',
      key: 'proveedor_nombre',
      width: 150,
      ellipsis: true,
      render: (_, record) => record.proveedor?.nombre_comercial
        ? <Tag color="purple">{record.proveedor.nombre_comercial}</Tag>
        : <Text type="secondary">—</Text>
    },
    {
      title: 'Unidad',
      dataIndex: 'unidad_medida',
      key: 'unidad_medida',
      width: 80,
      align: 'center',
      render: (u) => <Tag color="default">{u}</Tag>
    },
    {
      title: 'Stock Actual',
      dataIndex: 'stock_actual',
      key: 'stock_actual',
      width: 120,
      align: 'right',
      sorter: (a, b) => parseFloat(a.stock_actual) - parseFloat(b.stock_actual),
      render: (val, record) => {
        const { color } = getStockColor(val, record.stock_minimo, record.stock_maximo);
        const max = parseFloat(record.stock_maximo) || 0;
        const sobre = max > 0 && parseFloat(val) >= max;
        return (
          <Tooltip title={sobre ? 'Sobre stock máximo permitido' : null}>
            <Text strong style={{ color }}>
              {parseFloat(val).toLocaleString('es-MX')}
              {sobre && ' ⚠'}
            </Text>
          </Tooltip>
        );
      }
    },
    {
      title: 'Stock Mín.',
      dataIndex: 'stock_minimo',
      key: 'stock_minimo',
      width: 100,
      align: 'right',
      render: (val) => parseFloat(val).toLocaleString('es-MX')
    },
    {
      title: 'Stock Máx.',
      dataIndex: 'stock_maximo',
      key: 'stock_maximo',
      width: 100,
      align: 'right',
      render: (val) => {
        const max = parseFloat(val) || 0;
        return max > 0
          ? max.toLocaleString('es-MX')
          : <Text type="secondary">—</Text>;
      }
    },
    {
      title: 'P. Compra',
      dataIndex: 'precio_compra',
      key: 'precio_compra',
      width: 110,
      align: 'right',
      render: (val) => formatCurrency(val)
    },
    {
      title: 'P. Venta',
      dataIndex: 'precio_venta',
      key: 'precio_venta',
      width: 110,
      align: 'right',
      render: (val) => formatCurrency(val)
    },
    {
      title: 'Estado',
      dataIndex: 'activo',
      key: 'activo',
      width: 90,
      align: 'center',
      filters: [
        { text: 'Activo', value: 1 },
        { text: 'Inactivo', value: 0 }
      ],
      onFilter: (value, record) => record.activo === value,
      render: (activo) =>
        activo ? (
          <Tag color="success">Activo</Tag>
        ) : (
          <Tag color="default">Inactivo</Tag>
        )
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 140,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Ver tendencia de costos">
            <Button
              size="small"
              icon={<LineChartOutlined />}
              onClick={() => navigate(`/tendencias-costos?producto=${record.id}`)}
            />
          </Tooltip>
          {canEdit && (
            <Tooltip title="Editar producto">
              <Button
                type="primary"
                ghost
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEditModal(record)}
              />
            </Tooltip>
          )}
          {canDelete && (
            <Popconfirm
              title="¿Desactivar producto?"
              description={`El producto "${record.nombre}" será marcado como inactivo.`}
              onConfirm={() => handleDelete(record)}
              okText="Sí, desactivar"
              cancelText="Cancelar"
              icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
              okButtonProps={{ danger: true }}
              disabled={!record.activo}
            >
              <Tooltip title={record.activo ? 'Desactivar producto' : 'Ya está inactivo'}>
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  disabled={!record.activo}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Productos</Title>
          <Text type="secondary">Gestión de productos e inventario</Text>
        </div>
      </div>

      {/* Filters & Actions */}
      <Card bordered={false} style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={10} lg={8}>
            <Input
              placeholder="Buscar por nombre, clave o proveedor..."
              prefix={<SearchOutlined />}
              allowClear
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if (!e.target.value) fetchProductos({ search: '' });
              }}
              onPressEnter={(e) => handleSearch(e.target.value)}
              onClear={() => handleSearch('')}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              placeholder="Filtrar por proveedor"
              allowClear
              showSearch
              style={{ width: '100%' }}
              value={proveedorFilter || undefined}
              onChange={handleProveedorFilter}
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {proveedores.map((p) => (
                <Option key={p.id} value={p.id}>{p.nombre_comercial}</Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Button
              icon={<SearchOutlined />}
              onClick={() => handleSearch(search)}
            >
              Buscar
            </Button>
          </Col>
          <Col>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => { setSearch(''); setProveedorFilter(null); fetchProductos({ search: '', proveedor_id: null }); }}
            >
              Limpiar
            </Button>
          </Col>
          {canEdit && (
            <Col flex="auto" style={{ textAlign: 'right' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openCreateModal}
              >
                Nuevo Producto
              </Button>
            </Col>
          )}
        </Row>
      </Card>

      {/* Table */}
      <Card bordered={false}>
        <Table
          dataSource={productos}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="small"
          scroll={{ x: 1250 }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            pageSizeOptions: ['10', '15', '25', '50'],
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} productos`,
            locale: { items_per_page: '/ página' }
          }}
          locale={{ emptyText: 'No se encontraron productos' }}
        />
      </Card>

      {/* Modal Create/Edit */}
      <Modal
        title={editingProduct ? `Editar Producto: ${editingProduct.clave}` : 'Nuevo Producto'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText={editingProduct ? 'Guardar Cambios' : 'Crear Producto'}
        cancelText="Cancelar"
        confirmLoading={saving}
        width={700}
        destroyOnClose
      >
        <Divider style={{ marginTop: 8 }} />
        <Form
          form={form}
          layout="vertical"
          requiredMark="optional"
          initialValues={{ activo: true, unidad_medida: 'PZA' }}
        >
          <Row gutter={16}>
            <Col xs={24} sm={10}>
              <Form.Item
                name="clave"
                label="Clave del Producto"
                rules={[
                  { required: true, message: 'La clave es requerida' },
                  { max: 50, message: 'Máximo 50 caracteres' },
                  { pattern: /^[A-Z0-9_\-]+$/, message: 'Solo letras mayúsculas, números, guiones y guión bajo' }
                ]}
                normalize={(value) => value?.toUpperCase()}
              >
                <Input
                  placeholder="Ej: PROD-001"
                  maxLength={50}
                  style={{ textTransform: 'uppercase' }}
                  onChange={(e) => {
                    form.setFieldValue('clave', e.target.value.toUpperCase());
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={14}>
              <Form.Item
                name="nombre"
                label="Nombre del Producto"
                rules={[
                  { required: true, message: 'El nombre es requerido' },
                  { max: 255, message: 'Máximo 255 caracteres' }
                ]}
              >
                <Input placeholder="Nombre completo del producto" maxLength={255} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="descripcion" label="Descripción">
            <TextArea
              placeholder="Descripción del producto (opcional)"
              rows={2}
              maxLength={1000}
              showCount
            />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="categoria_id" label="Categoría">
                <Select
                  placeholder="Selecciona una categoría"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option?.children?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {categorias.map((cat) => (
                    <Option key={cat.id} value={cat.id}>{cat.nombre}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="proveedor_id" label="Proveedor">
                <Select
                  placeholder="Selecciona un proveedor"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option?.children?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {proveedores.map((prov) => (
                    <Option key={prov.id} value={prov.id}>{prov.nombre_comercial}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="unidad_medida" label="Unidad de Medida" rules={[{ required: true }]}>
                <Select placeholder="Selecciona unidad">
                  {UNIDADES.map((u) => (
                    <Option key={u} value={u}>{u}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="unidad_tendencia"
                label="Unidad de Tendencia"
                tooltip="Unidad en la que el mercado mueve el costo de este producto. Ej: se compra por PZA pero el acero se cotiza por KG. Déjalo vacío si la tendencia se analiza en la misma unidad de compra."
              >
                <Select placeholder="(opcional)" allowClear>
                  {UNIDADES.map((u) => (
                    <Option key={u} value={u}>{u}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="factor_unidad_tendencia"
                label="Factor de Conversión"
                tooltip="Cuántas unidades de tendencia hay en 1 unidad de compra. Ej: si 1 PZA de acero pesa 12.5 KG, el factor es 12.5. El costo por KG = costo por PZA ÷ factor."
                dependencies={['unidad_tendencia']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const u = getFieldValue('unidad_tendencia');
                      if (u && !value) {
                        return Promise.reject(new Error('Indica el factor de conversión'));
                      }
                      if (value != null && value <= 0) {
                        return Promise.reject(new Error('Debe ser mayor a 0'));
                      }
                      return Promise.resolve();
                    }
                  })
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={4}
                  placeholder="Ej: 12.5"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={12} sm={6}>
              <Form.Item
                name="stock_minimo"
                label="Stock Mínimo"
                rules={[{ type: 'number', min: 0, message: 'Debe ser mayor o igual a 0' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={3}
                  placeholder="0"
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(v) => v.replace(/,/g, '')}
                />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Item
                name="stock_maximo"
                label="Stock Máximo"
                tooltip="0 = sin límite. Si stock_actual lo iguala o supera, se alertará en el dashboard."
                rules={[
                  { type: 'number', min: 0, message: 'Debe ser mayor o igual a 0' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const min = getFieldValue('stock_minimo') || 0;
                      if (value && value > 0 && value < min) {
                        return Promise.reject(new Error('Debe ser ≥ stock mínimo'));
                      }
                      return Promise.resolve();
                    }
                  })
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={3}
                  placeholder="0 = sin límite"
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(v) => v.replace(/,/g, '')}
                />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Item
                name="precio_compra"
                label="Precio de Compra"
                rules={[{ type: 'number', min: 0, message: 'Debe ser mayor o igual a 0' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  prefix="$"
                  placeholder="0.00"
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(v) => v.replace(/,/g, '')}
                />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Item
                name="precio_venta"
                label="Precio de Venta"
                rules={[{ type: 'number', min: 0, message: 'Debe ser mayor o igual a 0' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  prefix="$"
                  placeholder="0.00"
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(v) => v.replace(/,/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="activo" label="Estado" valuePropName="checked">
            <Switch
              checkedChildren="Activo"
              unCheckedChildren="Inactivo"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Productos;
